<template>
    <master-layout pageTitle="Analytics">
        <div class="analytics-page">
            <div class="section-card">
                <div class="card-content">
                    <div class="dateAndExportBtnWrapper">
                        <div>
                            <div class="title">Custom Date Range</div>
                            <div class="date-range">
                                <DateTime id="from" v-model="filters.from"
                                    :maxValidation="{ enabled: true, value: minDateDiffInDays, type: 'days' }"
                                    presentation="date" :timezone="filters.timezone" :format="DATE_FMT"
                                    class="date-picker" />
                                <span class="to-sep">–</span>
                                <DateTime id="to" v-model="filters.to"
                                    :min-validation="{ enabled: true, value: maxDateDiffInDays, type: 'days' }"
                                    :maxValidation="{ enabled: true, value: 0, type: 'days' }"
                                    :timezone="filters.timezone" presentation="date" :format="DATE_FMT"
                                    class="date-picker" />
                            </div>
                        </div>
                        <VButton v-if="hasAnalyticsData" connotation="cta" appearance="filled" label="Export Data" class="marginTop10P" @click="() => showCsvModal = true">
                            <template #icon><VIcon name="download-line" /></template>
                        </VButton>
                    </div>
                </div>
            </div>
            <div class="section-card">
                <div class="card-content">
                    <div class="filters-wrap">
                        <template v-for="(f, idx) in filters.list" :key="`filter-${idx}`">
                            <div class="filter-pair">
                                <div class="filter">
                                    <div class="label">Dimension</div>
                                    <VSelect class="vivid-select" appearance="ghost" fixed-dropdown
                                        v-model="filters.list[idx].dimension"
                                        :disabled="isDimDisabled(idx)"
                                        @change="(e) => onDimensionChange(filters.list[idx].dimension, idx)">
                                        <VOption v-for="opt in dimOptions(idx)" :key="opt.value"
                                            :value="opt.value" :text="opt.title" />
                                    </VSelect>
                                </div>
                                <div class="filter">
                                    <div class="label">Value</div>
                                    <SelectDropDown
                                        :options="[{ value: 'all', title: 'All' }, ...(valueOptions[f.dimension] || [])]"
                                        :selectedValueProp="filters.list[idx].value" placeholder="Select Value"
                                        @onSelectOption="({ selectedValue }) => onValueChange(selectedValue, idx)"
                                        :clientSideSearch="true" />
                                </div>
                            </div>
                            <VIcon v-if="idx < filters.list.length - 1" name="chevron-right-line"
                                class="pair-arrow" />
                        </template>
                        <div class="reset" @click="resetFilters">
                            <VIcon name="refresh-line" />
                            <button class="reset-btn">Reset</button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="section-card">
                <div class="card-content chart-card-content">
                    <CustomChart :data="trafficChartPayload" title="Traffic" :legend="true" class="traffic-chart"
                        :legend-options="{ position: 'bottom', align: 'start', padding: 20, maxLabelLength: 20, bottomPadding: 10 }" />
                </div>
            </div>
            <div class="section-card">
                <div class="card-content table-card-content">
                    <div class="table-header-row">
                        <div class="table-title">Traffic (Table)</div>
                    </div>
                    <CustomChart :data="trafficTablePayload" class="traffic-table" />
                </div>
            </div>
        </div>
        <ExportCSVAnalytics :is-open="showCsvModal" :title="'Export Analytics Data'"
            @close="() => showCsvModal = false" :start-date="filters.from" :end-date="filters.to"
            :timezone="filters.timezone" :dateFormat="DATE_FMT" />
    </master-layout>
</template>

<script>
import DateTime from "@/components/DateTime.vue";
import apiInterceptor from "@/shared/apiInterceptor";
import moment from "moment-timezone";
import CustomChart from "@/components/CustomChart.vue";
import SelectDropDown from "@/components/selectDropDown.vue";
import ExportCSVAnalytics from "@/pages/developer-portal/analytics/exportCSVAnalytics.vue";
import { useUIStore } from '@/stores/ui';
import { environment } from '@/shared/env.js';

const DIMENSIONS = [
    { value: "developer_app", title: "Apps" },
    { value: "api_product", title: "Capabilities" },
    { value: "request_uri", title: "API" },
    { value: "response_status_code", title: "Status Code" },
];

export default {
    name: "AnalyticsPage",
    components: { DateTime, CustomChart, SelectDropDown, ExportCSVAnalytics },

    data() {
        const DATE_FMT = "MM/DD/YYYY";
        const deviceTz =
            moment.tz.guess() || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
        return {
            DATE_FMT,
            filters: {
                from: null,
                to: null,
                timezone: deviceTz,
                list: [{ dimension: DIMENSIONS[0].value, value: "all" }],
            },
            valueOptions: {},
            valuesLoading: {},
            valueFetchError: {},
            rawByDimension: {},
            trafficChartPayload: {
                loading: true,
                data: {
                    size: 0,
                    result: { chart_type: "multiline", labels: [], datasets: [] },
                    fields_to_rename: { xAxis: { label: " " }, yAxis: { label: " " } },
                },
            },
            trafficTablePayload: {
                loading: true,
                data: {
                    size: 0,
                    result: { chart_type: "table", data: [] },
                    fields_to_rename: {},
                },
            },
            maxDateDiffInDays: null,
            minDateDiffInDays: null,
            mounted: false,
            pendingAnalyticsRequestId: 0,
            showCsvModal: false,
        };
    },

    methods: {
        handleDateChange() {
            const { from, to } = this.filters;
            const today = moment();
            const start = moment(from, this.DATE_FMT);
            const end = moment(to, this.DATE_FMT);
            if (!start.isValid() || !end.isValid() || !today.isValid()) {
                this.maxDateDiffInDays = null;
                this.minDateDiffInDays = null;
                return;
            }
            const diffInDays = end.diff(start, "days");
            if (diffInDays > 90) {
                this.maxDateDiffInDays = null;
                this.minDateDiffInDays = null;
                useUIStore().updateToast({
                    open: true,
                    type: "danger",
                    message: "Date range cannot exceed 90 days.",
                });
                return;
            }
            this.maxDateDiffInDays = start.diff(today, "days");
            this.minDateDiffInDays = end.diff(today, "days");
            if (this.mounted) this.fetchAnalytics(this.filters.list[this.filters.list.length - 1].dimension);
        },

        resetPageState() {
            const tz = this.filters.timezone || "UTC";
            const today = moment().tz(tz).startOf("day");
            this.filters = {
                from: today.format(this.DATE_FMT),
                to: today.format(this.DATE_FMT),
                timezone: tz,
                list: [{ dimension: DIMENSIONS[0].value, value: "all" }],
            };
            this.valueOptions = {};
            this.valuesLoading = {};
            this.valueFetchError = {};
            this.rawByDimension = {};
            this.maxDateDiffInDays = null;
            this.minDateDiffInDays = null;
            this.trafficChartPayload = {
                loading: true,
                data: {
                    size: 0,
                    result: { chart_type: "multiline", labels: [], datasets: [] },
                    fields_to_rename: { xAxis: { label: " " }, yAxis: { label: " " } },
                },
            };
            this.trafficTablePayload = {
                loading: true,
                data: {
                    size: 0,
                    result: { chart_type: "table", data: [] },
                    fields_to_rename: {},
                },
            };
        },

        isNumeric(val) {
            if (val === null || val === undefined) return false;
            return typeof val === "number" || (typeof val === "string" && val.trim() !== "" && !Number.isNaN(Number(val)));
        },
        buildFilterQuery() {
            const parts = (this.filters?.list || [])
                .filter((r) => r?.dimension && r?.value && r.value !== "all")
                .map((r) => {
                    const v = String(r.value).replace(/;/g, "\\;");
                    return `${r.dimension}=${v}`;
                });
            if (!parts.length) return "";
            return parts.join(";");
        },
        async fetchAnalytics(dimension) {
            if (!dimension) return;
            const requestId = ++this.pendingAnalyticsRequestId;
            useUIStore().updateLoading({ isLoading: true, message: 'Loading analytics data...' });
            this.valuesLoading[dimension] = true;
            this.valueFetchError[dimension] = null;
            this.trafficChartPayload.loading = true;
            this.trafficTablePayload.loading = true;
            this.rawByDimension = {};
            const filterExpr = this.buildFilterQuery();
            const filterParam = filterExpr ? `&filter_string=${encodeURIComponent(filterExpr)}` : "";
            const start = moment
                .tz(this.filters.from, this.DATE_FMT, this.filters.timezone)
                .startOf("day")
                .utc();
            const end = moment
                .tz(this.filters.to, this.DATE_FMT, this.filters.timezone)
                .endOf("day")
                .utc();
            const dateFilter = `start_date=${encodeURIComponent(start.format("MM/DD/YYYY HH:mm"))}&end_date=${encodeURIComponent(end.format("MM/DD/YYYY HH:mm"))}`;
            const differenceInDays = end.diff(start, "days");
            try {
                const { Response } = await apiInterceptor.get(
                    environment("VUE_APP_APIGEE_API_URL") +
                    `analytics?` +
                    `${dateFilter}` +
                    `&dimension=${encodeURIComponent(dimension)}${filterParam}` +
                    `&time_unit=${differenceInDays <= 3 ? "hour" : "day"}`
                );
                if (requestId !== this.pendingAnalyticsRequestId) return;
                const res = Response;
                const normalized = this.normalizeAnalytics(res);
                this.rawByDimension[dimension] = normalized;
                this.valueOptions[dimension] = this.extractValuesForDimension(normalized, dimension);
                this.rebuildChart();
            } catch (err) {
                if (requestId !== this.pendingAnalyticsRequestId) return;
                this.valueFetchError[dimension] = err?.response?.data?.detail || "Failed to load values";
                this.valueOptions[dimension] = [];
                this.rebuildChart();
                useUIStore().updateToast({
                    open: true,
                    type: "danger",
                    message: `Failed to load values for ${dimension}`,
                });
            } finally {
                if (requestId === this.pendingAnalyticsRequestId) {
                    this.valuesLoading[dimension] = false;
                    this.trafficChartPayload.loading = false;
                    this.trafficTablePayload.loading = false;
                    useUIStore().updateLoading({ isLoading: false });
                }
            }
        },
        normalizeAnalytics(obj) {
            return {
                TimeUnit: Array.isArray(obj?.TimeUnit) ? obj.TimeUnit : [],
                stats: {
                    data: Array.isArray(obj?.stats?.data) ? obj.stats.data : [],
                },
            };
        },
        extractValuesForDimension(analytics, dimension) {
            const set = new Set();
            for (const row of analytics.stats.data) {
                const names = row?.identifier?.names || [];
                const vals = row?.identifier?.values || [];
                const idx = names.indexOf(dimension);
                if (idx !== -1 && vals[idx] != null) {
                    set.add(String(vals[idx]));
                }
            }
            return Array.from(set).map((v) => ({ title: v, value: v }));
        },
        nextUnusedDim() {
            const used = new Set(this.filters.list.map((r) => r.dimension).filter(Boolean));
            const next = DIMENSIONS.find((d) => !used.has(d.value));
            return next?.value || null;
        },
        pruneAfter(idx) {
            if (idx < this.filters.list.length - 1) {
                this.filters.list.splice(idx + 1);
            }
        },
        dimOptions(idx) {
            const current = this.filters.list[idx]?.dimension || null;
            const usedBefore = new Set(
                this.filters.list
                    .slice(0, idx)
                    .map((r) => r.dimension)
                    .filter(Boolean)
            );
            return DIMENSIONS.filter((d) => !usedBefore.has(d.value) || d.value === current).map((d) => ({ ...d, disabled: false }));
        },
        isDimDisabled(idx) {
            const isLast = idx === this.filters.list.length - 1;
            if (!isLast) return false;
            const opts = this.dimOptions(idx);
            const enabled = opts.filter((o) => !o.disabled);
            const current = this.filters.list[idx]?.dimension || null;
            if (enabled.length === 0) return true;
            return enabled.length === 1 && enabled[0]?.value === current;
        },
        async onDimensionChange(selectedValue, idx) {
            const row = this.filters.list[idx];
            row.dimension = selectedValue;
            row.value = "all";
            this.pruneAfter(idx);
            await this.fetchAnalytics(selectedValue);
        },
        async onValueChange(selectedValue, idx) {
            const row = this.filters.list[idx];
            row.value = selectedValue;
            if (selectedValue === "all") {
                this.pruneAfter(idx);
                this.fetchAnalytics(row.dimension);
                return;
            }
            const isLast = idx === this.filters.list.length - 1;
            const next = this.nextUnusedDim();
            if (isLast) {
                if (next) {
                    this.filters.list.push({ dimension: next, value: "all" });
                    this.fetchAnalytics(next);
                } else {
                    this.fetchAnalytics(row.dimension);
                }
            } else {
                this.filters.list[idx + 1] = { ...this.filters.list[idx + 1], value: "all" };
                if (idx + 1 < this.filters.list.length - 1) {
                    this.filters.list.splice(idx + 2);
                }
                this.fetchAnalytics(this.filters.list[idx + 1].dimension);
            }
        },
        resetFilters() {
            this.filters.list = [{ dimension: DIMENSIONS[0].value, value: "all" }];
            this.fetchAnalytics(this.filters.list[0].dimension);
        },
        rebuildChart() {
            let labels = [];
            for (const d of Object.keys(this.rawByDimension)) {
                const raw = this.rawByDimension[d];
                if (raw?.TimeUnit?.length) {
                    const differenceInDays = Math.abs(this.maxDateDiffInDays) - Math.abs(this.minDateDiffInDays);
                    labels = raw.TimeUnit.map((ms) => this.formatMsToLabel(ms, differenceInDays));
                    break;
                }
            }
            const datasets = [];

            for (const row of this.filters.list) {
                const dim = row.dimension;
                const val = row.value;
                const raw = this.rawByDimension[dim];
                if (!raw) continue;

                const series = raw.stats.data.filter((s) => {
                    const names = s?.identifier?.names || [];
                    const values = s?.identifier?.values || [];
                    const idx = names.indexOf(dim);
                    if (idx === -1) return false;
                    if (val === "all") return true;
                    return String(values[idx]) === String(val);
                });

                for (const s of series) {
                    const label = s?.identifier?.values && s.identifier.values[0] ? String(s.identifier.values[0]) : "Series";
                    const datapoints = s?.metric?.[0]?.values || [];
                    datasets.push({ label, data: datapoints });
                }
            }
            const size = datasets.reduce((acc, ds) => acc + (ds.data?.length || 0), 0);
            this.trafficChartPayload = {
                loading: false,
                data: {
                    size,
                    result: { chart_type: "multiline", labels, datasets },
                    fields_to_rename: { xAxis: { label: " " }, yAxis: { label: " " } },
                },
            };
            const toHeaderKey = (label) => {
                return /^\d+$/.test(label) ? `${label}\u200B` : label;
            };
            const tableRows = labels.map((label, idx) => {
                const row = { Time: label };
                datasets.forEach((ds) => {
                    const headerKey = toHeaderKey(ds.label);
                    row[headerKey] = ds.data?.[idx] ?? 0;
                });
                return row;
            });
            const tableSize = tableRows.length;
            this.trafficTablePayload = {
                loading: false,
                data: {
                    size: tableSize,
                    result: { chart_type: "table", data: tableRows },
                    fields_to_rename: {},
                },
            };
        },
        formatMsToLabel(ms, spanDays = 0) {
            const n = Number(ms);
            if (Number.isNaN(n)) return ms;
            const d = new Date(n);
            const month = d.toLocaleString("en-US", { month: "short" });
            const day = d.getDate();
            const year = d.getFullYear();
            const shortDate = d.toLocaleString("en-US", { day: "numeric", hour: "numeric", minute: "numeric", month: "short", hour12: true });
            if (spanDays < 3) {
                return shortDate;
            } else {
                return `${month} ${day}, ${year}`;
            }
        },
    },
    watch: {
        "filters.from"() { this.handleDateChange(); },
        "filters.to"() { this.handleDateChange(); },
    },
    async ionViewWillEnter() {
        const firstDim = this.filters.list[0]?.dimension;
        if (firstDim) {
            useUIStore().updateLoading({ isLoading: true, message: 'Loading analytics...' });
            await this.fetchAnalytics(firstDim);
            this.mounted = true;
            useUIStore().updateLoading({ isLoading: false });
        }
    },
    ionViewWillLeave() { this.resetPageState(); },
    computed: {
        hasAnalyticsData() {
            const chartHasData = this.trafficChartPayload?.data?.result?.datasets?.length > 0;
            const tableHasData = this.trafficTablePayload?.data?.result?.data?.length > 0;
            return chartHasData || tableHasData;
        }
    }
};
</script>

<style scoped lang="scss">
.analytics-page {
    padding: 16px;
}

.section-card {
    border-radius: 12px;
    box-shadow: none;
    margin-bottom: 12px;
    border: 1px solid var(--background-primary);
    background: var(--white);
}

.card-content {
    padding: 20px;
}

.date-range {
    display: flex;
    gap: 12px;
    align-items: center;
    margin-top: 6px;

    .to-sep {
        color: var(--gray-04);
    }
}

.reset {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--black);
    cursor: pointer;

    .reset-btn {
        background: transparent;
        border: none;
        padding: 0;
        color: inherit;
        cursor: pointer;
    }
}

.chart-card-content {
    display: flex;
    flex-direction: column;
}

.table-card-content {
    display: flex;
    flex-direction: column;
}

.traffic-chart {
    padding: 0px;
}

:deep(.traffic-chart > .title) {
    font-size: 18px;
    margin: 0px;
}

:deep(.traffic-table > .title) {
    font-size: 18px;
    margin: 0px;
}

.traffic-table {
    padding: 0px;
    height: auto;
    padding-bottom: 20px;
}

.table-title {
    font-weight: bold;
    color: var(--black);
    font-size: 18px;
}

.table-header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 20px 10px;
}

.filters-wrap {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 10px;
}

.filter-pair {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    align-items: end;
}

.label {
    color: var(--black);
    font-weight: 700;
    font-size: 12px;
}

.filter {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 200px;
    width: 200px;
}

.vivid-select {
    background: var(--background-primary);
    border-radius: 5px;
    min-height: 40px;
    width: 100%;
}

.pair-arrow {
    font-size: 22px;
    color: var(--gray-04);
    align-self: center;
    padding-top: 20px;
}

.dateAndExportBtnWrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
}

@media (max-width: 600px) {
    .filters-wrap {
        flex-direction: column;
        align-items: flex-start;
    }

    .pair-arrow {
        transform: rotate(90deg);
        margin: 6px 0;
        align-self: center;
        padding-top: 0px;
    }

    .filter-pair {
        grid-template-columns: 1fr;
        width: 100%;
    }

    .filter {
        width: 100%;
    }
}
</style>
